<script setup lang="ts">
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/landingPage/ui/card";

import LinkedInIcon from "@/icons/LinkedInIcon.vue";
import GithubIcon from "@/icons/GithubIcon.vue";
import XIcon from "@/icons/XIcon.vue";

// Team images (from src/assets/team)
import aurelieImg from "@/assets/team/aurelie-sigrand.jpg";
import margauxImg from "@/assets/team/margaux-dacquet.jpg";
import marieImg from "@/assets/team/marie-lourdais.jpg";
import vladImg from "@/assets/team/vlad-zaharia.jpg";
import charlesImg from "@/assets/team/charles-fassel.jpg";
import eliyanImg from "@/assets/team/eliyan-dochev.jpg";
import thomasImg from "@/assets/team/thomas-fiancette.jpg";
import vasilyImg from "@/assets/team/vasily-novikov.jpg";
import gabrielImg from "@/assets/team/gabriel-lepinay.jpg";
import anonymeImg from "@/assets/team/anonyme.jpg";

interface TeamProps {
  imageUrl: string;
  firstName: string;
  lastName: string;
  positions: string[];
  socialNetworks: SocialNetworkProps[];
}

interface SocialNetworkProps {
  name: string;
  url: string;
}

const teamList: TeamProps[] = [
  {
    imageUrl: vladImg,
    firstName: "Vlad",
    lastName: "Zaharia",
    positions: ["Project Manager Front-end Developer"],
    socialNetworks: [],
  },
  {
    imageUrl: thomasImg,
    firstName: "Thomas",
    lastName: "Fiancette",
    positions: ["Front-end Developer API & data integration"],
    socialNetworks: [],
  },
  {
    imageUrl: charlesImg,
    firstName: "Charles",
    lastName: "Fassel",
    positions: ["Front-end Developer Game design & integration"],
    socialNetworks: [],
  },
  {
    imageUrl: eliyanImg,
    firstName: "Eliyan",
    lastName: "Dochev",
    positions: ["Back-End Developer API & database"],
    socialNetworks: [],
  },
  {
    imageUrl: anonymeImg,
    firstName: "Vasily",
    lastName: "Novikov",
    positions: ["DevOps"],
    socialNetworks: [],
  },
  {
    imageUrl: gabrielImg,
    firstName: "Gabriel",
    lastName: "Lepinay",
    positions: ["Cybersecurity"],
    socialNetworks: [],
  },
  {
    imageUrl: aurelieImg,
    firstName: "Aurélie",
    lastName: "Sigrand",
    positions: ["Fondatrice association Ikigai"],
    socialNetworks: [],
  },
  {
    imageUrl: margauxImg,
    firstName: "Margaux",
    lastName: "Dacquet",
    positions: ["Spécialiste dans l'orientation des jeunes"],
    socialNetworks: [],
  },
  {
    imageUrl: marieImg,
    firstName: "Marie",
    lastName: "Lourdais",
    positions: ["Psychologue TSA"],
    socialNetworks: [],
  },
];

const socialIcon = (socialName: string) => {
  switch (socialName) {
    case "LinkedIn":
      return LinkedInIcon;

    case "Github":
      return GithubIcon;

    case "X":
      return XIcon;
  }
};
</script>

<template>
  <section
    id="team"
    class="container lg:w-[50%] mx-auto py-24 sm:py-32"
  >
    <div class="text-center mb-8">
      <h2 class="text-lg text-[#ff9500] text-center mb-2 tracking-wider">Équipe</h2>

      <h2 class="text-3xl md:text-4xl text-center font-bold">
        Une équipe pluridisciplinaire engagée
      </h2>
    </div>

    <div
      class="flex flex-wrap gap-6 justify-center"
    >
      <Card
        v-for="{
          imageUrl,
          firstName,
          lastName,
          positions,
          socialNetworks,
        } in teamList"
        :key="imageUrl"
        class="bg-muted/60 dark:bg-card flex flex-col overflow-hidden group/hoverimg w-full max-w-[200px] h-[380px]"
      >
        <CardHeader class="p-0 gap-0">
          <div class="aspect-square overflow-hidden bg-muted/30 flex items-center justify-center">
            <img
              :src="imageUrl"
              :alt="`${firstName} ${lastName}`"
              class="w-full h-full object-contain saturate-0 transition-all duration-200 ease-linear group-hover/hoverimg:saturate-100 group-hover/hoverimg:scale-[1.01]"
            />
          </div>
          <CardTitle class="py-6 pb-4 px-6"
            >{{ firstName }}
            <span class="text-[#ff9500]">{{ lastName }}</span>
          </CardTitle>
        </CardHeader>

        <CardContent
          v-for="(position, index) in positions"
          :key="index"
          :class="{
            'pb-0 text-muted-foreground ': true,
            'pb-4': index === positions.length - 1,
          }"
        >
          {{ position }}<span v-if="index < positions.length - 1">,</span>
        </CardContent>

        <CardFooter class="space-x-4 mt-auto">
          <a
            v-for="{ name, url } in socialNetworks"
            :key="name"
            :href="url"
            target="_blank"
            class="hover:opacity-80 transition-all"
            :aria-label="`Visit our ${name} page`"
          >
            <component :is="socialIcon(name)" />
          </a>
        </CardFooter>
      </Card>
    </div>
  </section>
</template>
